/**
 * queueSource — which work the Assignment Board is allowed to hand out.
 *
 * The board used to queue `BQC1 || BQC2` as one list. Those two stages are not
 * the same kind of work, and merging them let a planner dispatch a bundle that
 * had not been cut yet. Six hundred queued bundles looked like six hundred
 * dispatchable bundles; a large share of them were fabric that no cutter had
 * touched.
 *
 * The split is already in the data. The importer classifies a row by the
 * furthest stage sheet it appears on, cascading downward:
 *
 *   qcDate                     → DONE
 *   rework rows                → BQC5
 *   receivedDate / receivedQty → BQC4
 *   assignedDate / assignedQty → BQC3   ← a sewer took it
 *   cutDate / cutQty           → BQC2   ← a cutter scanned it, nobody has it
 *   (nothing above)            → BQC1   ← never cut
 *
 * So the two pools fall straight out of `currentStage`:
 *
 *   CUT     scanned at the BQC3 station. The queue used to trust the BQC2
 *           sheet here; it no longer does. A bundle enters Cut · ready when
 *           someone physically holds it under the BQC3 scanner — the scan
 *           log (iSyn2_CutReadyScans) is real-time and the workbook import
 *           is not. A BQC2-stage row with no scan is "cut per the workbook,
 *           awaiting BQC3 scan": visible as a count, not queueable.
 *
 *   PRECUT  stage BQC1, unscanned — on the planning sheet and nowhere
 *           downstream. Fabric and components. Assigning it is forward
 *           planning, not dispatch. A scan outranks the stage: the workbook
 *           lagging behind the floor is exactly the failure the scan log
 *           exists to correct, so a scanned BQC1-stage row queues as CUT.
 *
 * A stage of BQC3 or beyond still excludes the row from both pools whatever
 * the scan log says — reaching BQC3 means a sewer took the bundle, and a
 * leftover scan row must wash out rather than offer the work twice. That
 * only holds when the stage is backed by a REAL hand-off, though —
 * `dispatchedAt` (a BQC3 row) or `receivedAt` (a BQC4 row) actually present.
 * BQC1's own PACKAGING/INBOUNDED columns can push a WIP straight to DONE
 * with zero downstream scan evidence at all (see classifyStage's
 * doneViaPacked/doneViaInbound in parseProductionGridWorkbook.ts) — a
 * shortcut for genuinely pre-existing finished goods, not proof a sewer
 * ever touched THIS bundle. A live BQC3 scan against a WIP with neither
 * stamp set is a live contradiction of that shortcut (confirmed against
 * WIPs 26080629/26080254, 2026-08-19: currentStage DONE, notes "PACKED —
 * … not found in BQC2–BQC6", no dispatchedAt/receivedAt, yet a real
 * Cut·Ready scan in hand) — the scan is ground truth of physical
 * possession and must win, same principle Dispatch·BQC3's own Cut·Ready
 * table already applies ("a Cut Ready scan means the bundle is physically
 * in hand … that is ground truth the office-side status columns cannot
 * override").
 *
 * The pools do still need the app-side unassigned filter. The board's own
 * overlay can assign a sewer without the workbook moving, and the BQC1 sheet
 * carries its own SEWER column a planner may have filled in by hand — neither
 * shifts `currentStage`. Callers therefore keep their existing unassigned
 * filter; this module answers only "which pool", never "is it free".
 */

/** Which of the two queues a row belongs to. */
export type QueueSource = "CUT" | "PRECUT";

export const QUEUE_SOURCES: readonly QueueSource[] = ["CUT", "PRECUT"] as const;

/**
 * How far along a pre-cut bundle is.
 *
 * A row only reaches the BQC2 sheet when the cutter scans it as done, so from
 * the grid alone "on the table right now" and "not started" are the same
 * stage. What separates them is the CUTTER column: the planning sheet records
 * the cutting line (FAST, STEADY 2, FLEX) a WIP has been allocated to before
 * any cutting happens. An allocated line is the closest thing the data has to
 * "cutting is under way" — it is an inference from allocation, not a scan, and
 * is labelled that way on screen rather than being dressed up as progress.
 */
export type CutState = "ON_LINE" | "NOT_STARTED";

/**
 * Values the CUTTER column uses to mean "nobody". The column is hand-kept and
 * "0" is common; treating it as a cutting line would report unstarted work as
 * being on the table.
 */
const NO_CUTTER = new Set(["", "0", "-", "--", "—", "N/A", "NA", "NONE", "TBA", "TBD", "NULL"]);

/** True when the row names a cutting line it has been allocated to. */
export function hasCuttingLine(cutter: string | null | undefined): boolean {
  return !NO_CUTTER.has((cutter ?? "").trim().toUpperCase());
}

export function cutStateOf(cutter: string | null | undefined): CutState {
  return hasCuttingLine(cutter) ? "ON_LINE" : "NOT_STARTED";
}

/**
 * The pool a stage belongs to, or null when the row is past the point of being
 * assignable at all — BQC3 onward is already with a sewer or already back.
 *
 * This is the workbook-only view. The queue itself is built with
 * `classifyQueueRow`, which layers the BQC3 scan log on top; this stays
 * exported because "is the stage assignable at all" is still a question on
 * its own (and it is the gate classifyQueueRow applies first).
 */
export function sourceOfStage(stage: string | null | undefined): QueueSource | null {
  if (stage === "BQC2") return "CUT";
  if (stage === "BQC1") return "PRECUT";
  return null;
}

/**
 * Which queue a row belongs to, scan log included — the classification the
 * board actually uses.
 *
 *   stage BQC3+, real hand-off   → null    a sewer took it; a stale scan
 *                                          must not resurrect it (work
 *                                          offered twice)
 *   stage BQC3+, NO hand-off,
 *     scanned                    → CUT     DONE/BQC3+ came from a
 *                                          completion marker with nothing
 *                                          behind it; the scan is the only
 *                                          real evidence and outranks it
 *   scanned (stage BQC1/BQC2)    → CUT     the station physically saw the
 *                                          bundle; the scan outranks the
 *                                          workbook stage
 *   stage BQC1, no scan          → PRECUT  planning work, unchanged
 *   stage BQC2, no scan          → null    cut per the workbook but not yet
 *                                          through the BQC3 scan — surfaced
 *                                          as an "awaiting scan" count,
 *                                          never queued
 *
 * `hasGenuineHandoff` is `!!(wip.dispatchedAt || wip.receivedAt)` — both are
 * read strictly off their own BQC3/BQC4 sheet rows (never a fallback
 * marker), so either one being set is real proof a sewer actually took or
 * returned the bundle. See the module header for why a stage reached
 * without either of those stamps must not wash out a live scan.
 */
export function classifyQueueRow(
  stage: string | null | undefined,
  hasScan: boolean,
  hasGenuineHandoff: boolean
): QueueSource | null {
  const base = sourceOfStage(stage);
  if (base === null) return hasScan && !hasGenuineHandoff ? "CUT" : null;
  if (hasScan) return "CUT";
  return base === "PRECUT" ? "PRECUT" : null;
}

/**
 * Test scans — end-to-end rehearsal fodder while the valve is at dry-run.
 *
 * The station's Test mode scans OLD WIPs (rows the workbook already moved to
 * BQC3+/DONE) so there is plenty to practise on without waiting for fresh
 * cutting. Those scans are tagged in scannedBy rather than in a schema field:
 * iSyn2_CutReadyScans is already provisioned, and provisioning never adds
 * columns to an existing list (schema-pollution ban). The tag is visible in
 * the row's own history, which is the point — test data must say so.
 */
export const TEST_SCAN_TAG = "[TEST]";

export function isTestScan(scannedBy: string | null | undefined): boolean {
  return (scannedBy ?? "").trimEnd().endsWith(TEST_SCAN_TAG);
}

/** The fields the scan reducer needs — structural, so the pure logic here
 * never imports the service layer. */
export interface IScanLike {
  wipId: string;
  scannedAt: string;
  undoneAt?: string;
}

/**
 * Latest live scan per WIP. Undone rows are dropped BEFORE picking the
 * latest, so scan → undo → rescan resolves to the rescan, and scan → undo
 * resolves to nothing — an undone mis-scan must not keep a bundle queued.
 */
export function activeScansByWip<S extends IScanLike>(scans: readonly S[]): Map<string, S> {
  const out = new Map<string, S>();
  for (const s of scans) {
    if (s.undoneAt) continue;
    const key = s.wipId.trim();
    if (!key) continue;
    const prev = out.get(key);
    if (!prev || s.scannedAt >= prev.scannedAt) out.set(key, s);
  }
  return out;
}

export interface IQueueSourceMeta {
  /** Segmented-control label. */
  label: string;
  /** Long-form, for a tooltip and the settings screen. */
  detail: string;
  /** Which stage sheet backs it, shown so the figure can be traced. */
  stage: string;
}

export const QUEUE_SOURCE_META: Readonly<Record<QueueSource, IQueueSourceMeta>> = {
  CUT: {
    label: "Cut · ready",
    detail:
      "Bundles physically scanned at the BQC3 dispatch station and not yet " +
      "assigned. The scan is the source — a bundle enters this queue the moment " +
      "the station scans it, so the list is real-time and every WIP in it is " +
      "confirmed in-house. Cut work the workbook knows about but the station has " +
      "not scanned yet is counted separately as awaiting scan.",
    stage: "BQC3 scan",
  },
  PRECUT: {
    label: "Pre-cut · planning",
    detail:
      "Bundles still on the BQC1 planning sheet with no BQC3 scan. The components " +
      "have not been cut, so an assignment here reserves a sewer's future capacity " +
      "rather than handing over work. Use it to see whether the plotted weeks can " +
      "absorb what is coming.",
    stage: "BQC1",
  },
};

export interface IPoolCount {
  bundles: number;
  sets: number;
}

export interface IQueueSourceSummary {
  CUT: IPoolCount;
  PRECUT: IPoolCount;
  /** Of the pre-cut pool, how much is allocated to a cutting line already. */
  onLine: IPoolCount;
  notStarted: IPoolCount;
}

const zero = (): IPoolCount => ({ bundles: 0, sets: 0 });

/**
 * Bundle and set totals for both pools in one pass.
 *
 * The toggle badges both sides at once — a planner deciding whether to switch
 * needs to see what is on the other side before switching, not after.
 *
 * Takes rows that have ALREADY been classified, rather than re-deriving the
 * pool from the stage string. The board tags each row once when it builds the
 * queue; classifying a second time here would be the same decision made twice,
 * and two copies of a rule are two chances to disagree.
 *
 * Rows are counted exactly as handed in, so the caller applies its own
 * unassigned filter first and the badge always matches the list it opens.
 */
export function summariseQueueSources<T>(
  rows: readonly T[],
  read: (row: T) => { source: QueueSource | null; qty: number; onLine: boolean }
): IQueueSourceSummary {
  const out: IQueueSourceSummary = {
    CUT: zero(), PRECUT: zero(), onLine: zero(), notStarted: zero(),
  };
  for (const row of rows) {
    const { source, qty, onLine } = read(row);
    if (!source) continue;
    const sets = qty > 0 ? qty : 0;
    out[source].bundles += 1;
    out[source].sets += sets;
    if (source === "PRECUT") {
      const k = onLine ? "onLine" : "notStarted";
      out[k].bundles += 1;
      out[k].sets += sets;
    }
  }
  return out;
}

// ─── Cut age ────────────────────────────────────────────────────────────────

/**
 * Days a bundle has been sitting since its cut date.
 *
 * Cut work is meant to move to a sewer within days. A bundle cut months ago
 * and never dispatched is not a queue item — it is a row nobody closed out,
 * a re-cut under a new WIP, or a cancellation. Offering it as "ready" buries
 * the work that genuinely is.
 *
 * This is a DIFFERENT question from the stale-due-date filter already on the
 * rail. That one asks "how late is this against its promise"; this asks "how
 * long has the physical bundle been sitting". A bundle can be cut yesterday
 * and already overdue, or cut in April and not due until September — the two
 * filters catch different rows and both are needed.
 *
 * Returns null when there is no date to measure from, which must never be
 * read as fresh: a row with no cut date is a row with no evidence.
 */
export function cutAgeDays(cutDate: string | null | undefined, now: number = Date.now()): number | null {
  if (!cutDate) return null;
  const t = new Date(cutDate).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.floor((now - t) / 86_400_000);
}

/** Days of undispatched sitting after which cut work stops counting as ready. */
export const DEFAULT_CUT_MAX_AGE_DAYS = 30;

/**
 * True when a cut bundle has sat too long to be called ready.
 *
 * An undateable row counts as aged rather than fresh. The alternative — no
 * date, so assume it is recent — is how the original bug survived: an absent
 * signal was read as a positive one.
 */
export function isAgedCut(ageDays: number | null, maxAgeDays: number = DEFAULT_CUT_MAX_AGE_DAYS): boolean {
  if (maxAgeDays <= 0) return false; // guard disabled
  return ageDays === null || ageDays > maxAgeDays;
}

// ─── The dry-run intent log ────────────────────────────────────────────────

/** Enough of an intent-log row to reduce it. */
export interface IPickLike {
  spId: number;
  wipId: string;
  proposedSewer: string;
  runAt: string;
}

/**
 * The proposal a station should show for each WIP — the newest one.
 *
 * The log is APPEND-ONLY: every logged simulation writes a fresh row per WIP,
 * so a bundle simulated three times has three rows and this returns one. That
 * asymmetry is worth stating loudly, because it is what made "Clear proposals"
 * appear to do nothing: the button was driven by this map, deleted the
 * twenty-two rows it could see, and the row behind each one took its place on
 * the next load. Clearing has to walk the log, not the view of it.
 *
 * Lives here rather than on WorkcentersService so it can be tested — importing
 * that service starts MSAL.
 */
export function latestPickPerWip<P extends IPickLike>(picks: readonly P[]): Map<string, P> {
  const map = new Map<string, P>();
  for (const p of picks) {
    if (!p.wipId || !p.proposedSewer) continue;
    const prev = map.get(p.wipId);
    if (!prev || p.runAt > prev.runAt || (p.runAt === prev.runAt && p.spId > prev.spId)) {
      map.set(p.wipId, p);
    }
  }
  return map;
}
